import { useId, useRef, useState, type ReactNode } from 'react';
import { ImageError, readImage, type ImagePreset } from '../../utils/image';
import { PhotoCropper } from './PhotoCropper';
import { t } from '../../i18n';

/**
 * Choix d'une photo dans la galerie ou l'appareil.
 *
 * Le champ de fichier natif est illisible : on le cache et on declenche
 * son ouverture depuis l'element qu'on veut vraiment montrer — un avatar,
 * une pastille, un bouton du composeur.
 *
 * Le redimensionnement se fait avant de rendre la main : l'appelant ne
 * recoit qu'une data URL deja bornee, jamais un fichier brut.
 *
 * Une photo d'avatar — la sienne ou celle d'un groupe — passe d'abord par
 * le recadrage : elle finit dans un rond, et c'est a son proprietaire de
 * dire ce qu'on y voit. Le choix se fait sur le gabarit et non sur un
 * drapeau de plus a passer : les cinq ecrans qui changent une photo de
 * profil demandent tous `avatar`, et aucun de ceux qui ne l'ont pas ne
 * montre sa photo dans un cercle. Un drapeau se serait oublie au premier
 * ecran ajoute.
 *
 * Des photos, et rien d'autre : `accept` ne propose pas la video, ce qui
 * retire aussi l'entree « video » du menu natif d'iOS. Le fil sait
 * toujours lire les films deja envoyes — ils ne se font simplement plus.
 */
export function PhotoPicker({
  preset,
  label,
  onPicked,
  onError,
  className = '',
  disabled = false,
  children,
}: {
  preset: ImagePreset;
  /** Nom lu par les lecteurs d'ecran : "Changer ma photo de profil". */
  label: string;
  onPicked: (dataUrl: string) => void | Promise<void>;
  /** Prevenu quand le fichier est refuse : pas une image, trop lourd, illisible. */
  onError?: (message: string) => void;
  className?: string;
  disabled?: boolean;
  children: ReactNode;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  /** La photo en attente de cadrage, tant que « Choisir » n'a pas ete touche. */
  const [aCadrer, setACadrer] = useState<File | null>(null);

  async function onFile(file: File | undefined) {
    // Reinitialise le champ : rechoisir le meme fichier doit redeclencher.
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    // Le cadrage se choisit a la main, il ne se calcule pas.
    if (preset === 'avatar' && file.type.startsWith('image/')) {
      setACadrer(file);
      return;
    }

    setBusy(true);
    try {
      await onPicked(await readImage(file, preset));
    } catch (cause) {
      onError?.(
        cause instanceof ImageError || cause instanceof Error
          ? cause.message
          : t.composants.fichier.pasAjoute,
      );
    } finally {
      setBusy(false);
    }
  }

  /*
    Le recadrage ne se referme qu'une fois la photo partie : l'envoi passe
    par Storage, et voir revenir l'ecran precedent pendant que le rond est
    encore vide donnerait a croire que rien n'a ete pris. Un echec le
    laisse ouvert, avec le cadrage intact — il n'y a rien a refaire.
  */
  async function surChoix(dataUrl: string) {
    await onPicked(dataUrl);
    setACadrer(null);
  }

  return (
    <>
      <button
        type="button"
        className={className}
        aria-label={label}
        title={label}
        disabled={disabled || busy}
        aria-busy={busy}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </button>

      <input
        id={inputId}
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => void onFile(event.target.files?.[0])}
      />

      {aCadrer && (
        <PhotoCropper
          file={aCadrer}
          preset={preset}
          onCancel={() => setACadrer(null)}
          onChoose={surChoix}
          onError={onError}
        />
      )}
    </>
  );
}
